import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingsCargoOrderListingUserPermissionsHelper from "./common/user-permissions/billings-cargo-order-listing-user-permission.helper";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import BillingsCargoOrderListingViewMode from "./common/types/billings-cargo-order-listing-view-mode";
import ButtonComponent from "../../../../common/components/button/button.component";
import BillingsCargoOrderListingByRailyComponent from "./by-raily/billings-cargo-order-listing-by-raily.component";
import BillingsCargoOrderListingByCargoComponent from "./by-cargo/billings-cargo-order-listing-by-cargo.component";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";

type BillingsCargoOrderListingProps = {};

const BillingsCargoOrderListingComponent: FC<
  BillingsCargoOrderListingProps
> = () => {
  const { user, selectedAppLanguage, setBreadcrumbs } = useAppContext();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.billingsCargoOrderListing);

  const userPermissions = useMemo(
    () =>
      billingsCargoOrderListingUserPermissionsHelper.getPermissions(
        user?.roles!,
      ),
    [],
  );

  const translations = useMemo(
    () => billingsTranslationsHelper.getCargoOrderBillingsListingTranslations(),
    [selectedAppLanguage],
  );

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getCargoOrderListingBreadcrumbs();

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const [selectedViewMode, setSelectedViewMode] =
    useState<BillingsCargoOrderListingViewMode | null>(null);

  const ChangeViewToRailyButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsCargoOrderListingViewMode.RAILY)
        }
        title={translations.header.changeViewToRailyButtonTitle}
      >
        {translations.header.changeViewToRailyButtonText}
      </ButtonComponent>
    ),
    [translations],
  );

  const ChangeViewToCargoButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsCargoOrderListingViewMode.CARGO)
        }
        title={translations.header.changeViewToCargoButtonTitle}
      >
        {translations.header.changeViewToCargoButtonText}
      </ButtonComponent>
    ),
    [translations],
  );

  const viewChangeButtonOptions = useMemo(
    () => [
      {
        viewMode: BillingsCargoOrderListingViewMode.RAILY,
        buttonOptions: {
          button: ChangeViewToCargoButton,
          hasPermission: userPermissions.hasAccessToCargoView,
        },
      },
      {
        viewMode: BillingsCargoOrderListingViewMode.CARGO,
        buttonOptions: {
          button: ChangeViewToRailyButton,
          hasPermission: userPermissions.hasAccessToRailyView,
        },
      },
    ],
    [userPermissions, ChangeViewToCargoButton, ChangeViewToRailyButton],
  );

  const getViewChangeButtons = useCallback(
    (viewMode: BillingsCargoOrderListingViewMode) => {
      const buttonOptionsForSelectedViewMode = viewChangeButtonOptions
        .filter((option) => option.viewMode === viewMode)
        .map((option) => option.buttonOptions);

      return buttonOptionsForSelectedViewMode
        .filter((option) => option.hasPermission)
        .map((option) => option.button);
    },
    [viewChangeButtonOptions],
  );

  const viewOptions = useMemo(
    () => [
      {
        mode: BillingsCargoOrderListingViewMode.RAILY,
        component: (
          <BillingsCargoOrderListingByRailyComponent
            actionButtons={getViewChangeButtons(
              BillingsCargoOrderListingViewMode.RAILY,
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToRailyView,
      },
      {
        mode: BillingsCargoOrderListingViewMode.CARGO,
        component: (
          <BillingsCargoOrderListingByCargoComponent
            actionButtons={getViewChangeButtons(
              BillingsCargoOrderListingViewMode.CARGO,
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToCargoView,
      },
    ],
    [getViewChangeButtons],
  );

  const getPossibleViewOptions = useCallback(() => {
    return viewOptions.filter((option) => option.hasPermission);
  }, []);

  const possibleViewOptions = useMemo(
    () => getPossibleViewOptions(),
    [getPossibleViewOptions],
  );

  useEffect(() => {
    if (possibleViewOptions.length === 0) {
      return;
    }
    setSelectedViewMode(possibleViewOptions[0].mode);
  }, [possibleViewOptions]);

  const SelectedViewComponent = useMemo(
    () =>
      viewOptions.find((option) => option.mode === selectedViewMode)?.component,
    [selectedViewMode, viewOptions],
  );

  if (!SelectedViewComponent) {
    return null;
  }

  return SelectedViewComponent;
};

export default BillingsCargoOrderListingComponent;
